import { ReportingConfig } from '@opengov/app-config-reporting';
import { trace, context } from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { Resource } from '@opentelemetry/resources';
import { SEMRESATTRS_SERVICE_NAME, SEMRESATTRS_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { FaroSessionSpanProcessor, FaroTraceExporter } from '@grafana/faro-web-tracing';
import {
  getWebInstrumentations,
  initializeFaro,
  MetaUser,
  TransportItem,
  TransportItemType
} from '@grafana/faro-web-sdk';
import { ISessionV3 } from '@opengov/app-core/types';
import { ManualContextManager } from './ManualContextManager';
import {
  createReactRouterV6DataOptions,
  EventEvent,
  ExceptionEvent,
  Faro,
  LogEvent,
  MeasurementEvent,
  ReactIntegration
} from '@grafana/faro-react';
import { Entity } from '@opengov/bnp-core';
import { matchRoutes } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import type { EmptyObject, SetOptional } from 'type-fest';

const resource = Resource.default().merge(
  new Resource({
    [SEMRESATTRS_SERVICE_NAME]: 'reporting-app',
    [SEMRESATTRS_SERVICE_VERSION]: '1.0.0'
  })
);

const provider = new WebTracerProvider({ resource });

//Export for testing
export const buildUser = ({ session, entity }: { session?: ISessionV3; entity?: Entity }) => {
  const user: MetaUser = { attributes: { journey_id: Math.random().toString(36).slice(2) } };

  if (session) {
    user.email = session.email;
  }

  if (entity) {
    user.attributes = user.attributes ?? {};
    user.attributes.entity_id = entity.id;
    user.attributes.entity_subdomain = entity.subdomain;
    user.attributes.entity_name = entity.name;
    user.attributes.entity_uuid = entity.uuid;
  }

  return user;
};

export const contextManager = new ManualContextManager();

const appVersion = ReportingConfig.GITHUB_SHA;
if (process.env.NODE_ENV !== 'test' && appVersion) {
  // eslint-disable-next-line no-console
  console.log('og_reporting_app_version:', appVersion);
}

export function addMetadataToItem({
  item,
  entitySubdomain
}: {
  item: TransportItem<ExceptionEvent | MeasurementEvent | LogEvent>;
  entitySubdomain?: string;
}) {
  item.payload.context = {
    ...item.payload.context,
    ...(entitySubdomain && { entitySubdomain })
  };

  return item;
}

export function addMetadataToEventItem({
  item,
  entitySubdomain
}: {
  item: TransportItem<EventEvent>;
  entitySubdomain?: string;
}) {
  item.payload.attributes = {
    ...item.payload.attributes,
    ...(entitySubdomain && { entitySubdomain })
  };
  return item;
}

export function beforeSend({ entitySubdomain, item }: { item: TransportItem; entitySubdomain?: string }) {
  switch (item.type) {
    // We send custom context to measurement, log and exception event as they support
    case TransportItemType.EXCEPTION:
      return addMetadataToItem({ item: item as TransportItem<ExceptionEvent>, entitySubdomain });
    case TransportItemType.MEASUREMENT:
      return addMetadataToItem({ item: item as TransportItem<MeasurementEvent>, entitySubdomain });
    case TransportItemType.LOG: {
      return addMetadataToItem({ item: item as TransportItem<LogEvent>, entitySubdomain });
    }
    case TransportItemType.EVENT:
      return addMetadataToEventItem({ item: item as TransportItem<EventEvent>, entitySubdomain });
    case TransportItemType.TRACE: {
      return item;
    }
  }
}

type InitializationOptions = {
  collectorUrl: string;
  appName: string;
  entity?: Entity;
  session?: ISessionV3;
};

export function initializeGrafanaFaro({ collectorUrl, appName, entity, session }: InitializationOptions) {
  try {
    const faro = initializeFaro({
      url: collectorUrl,
      app: {
        name: appName,
        environment: process.env.NODE_ENV === 'production' ? process.env.CDN_BUILD_ENVIRONMENT : 'development',
        ...(appVersion ? { version: appVersion } : {})
      },
      user: buildUser({ session, entity }),
      isolate: false,
      instrumentations: [
        ...getWebInstrumentations(),
        new ReactIntegration({
          router: createReactRouterV6DataOptions({
            matchRoutes
          })
        })
      ],
      trackWebVitalsAttribution: true,
      beforeSend: (item) => beforeSend({ entitySubdomain: entity?.subdomain, item })
    });

    provider.addSpanProcessor(
      new FaroSessionSpanProcessor(new BatchSpanProcessor(new FaroTraceExporter({ ...faro })), faro.metas)
    );
    provider.register({
      propagator: new W3CTraceContextPropagator(),
      contextManager: contextManager
    });
    const ignoreUrls = [collectorUrl];

    const propagateTraceHeaderCorsUrls = [new RegExp(`${ReportingConfig.OPERATE_API_HOST}/`)];

    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({ ignoreUrls, ignoreNetworkEvents: true, propagateTraceHeaderCorsUrls }),
        new XMLHttpRequestInstrumentation({ ignoreUrls, propagateTraceHeaderCorsUrls })
      ]
    });
    faro.api.initOTEL(trace, context);

    return faro;
  } catch (error) {
    console.error('Failed to initialize Grafana Faro:', error);
    return null;
  }
}

type CorrectedFaro = SetOptional<Faro, 'api'>;
export function isClientInitialized(faro: CorrectedFaro | EmptyObject | null) {
  const isFaroEmpty = (faro: CorrectedFaro | EmptyObject): faro is EmptyObject => isEmpty(faro);

  if (isNil(faro) || isFaroEmpty(faro)) {
    return false;
  }

  return faro.api !== undefined;
}
